import { useState, useRef, useEffect } from 'react'
import { CalendarDays, ChevronLeft, ChevronRight, X } from 'lucide-react'
import { useDateRange, buildPreset, DateRangePreset, DateRange } from '../../context/DateRangeContext'

// ── Preset list ────────────────────────────────────────────────
const PRESETS: { id: DateRangePreset; label: string }[] = [
  { id: 'today',        label: 'Today' },
  { id: 'yesterday',    label: 'Yesterday' },
  { id: 'last7',        label: 'Last 7 days' },
  { id: 'last30',       label: 'Last 30 days' },
  { id: 'thisMonth',    label: 'This month' },
  { id: 'lastMonth',    label: 'Last month' },
  { id: 'thisQuarter',  label: 'This quarter' },
  { id: 'lastQuarter',  label: 'Last quarter' },
  { id: 'thisYear',     label: 'This year' },
  { id: 'lastYear',     label: 'Last year' },
  { id: 'allTime',      label: 'All time' },
  { id: 'custom',       label: 'Custom range' },
]

// ── Mini calendar ──────────────────────────────────────────────
const DAYS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa']
const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December']

function MiniCalendar({
  viewDate, onPrevMonth, onNextMonth,
  selectStart, selectEnd, hoverDate,
  onDayClick, onDayHover,
}: {
  viewDate: Date
  onPrevMonth: () => void
  onNextMonth: () => void
  selectStart: Date | null
  selectEnd: Date | null
  hoverDate: Date | null
  onDayClick: (d: Date) => void
  onDayHover: (d: Date) => void
}) {
  const year  = viewDate.getFullYear()
  const month = viewDate.getMonth()
  const firstDay = new Date(year, month, 1).getDay()
  const daysInMonth = new Date(year, month + 1, 0).getDate()

  const cells: (Date | null)[] = []
  for (let i = 0; i < firstDay; i++) cells.push(null)
  for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(year, month, d))

  function isStart(d: Date | null)  { return !!d && !!selectStart && d.toDateString() === selectStart.toDateString() }
  function isEnd(d: Date | null)    { return !!d && !!selectEnd   && d.toDateString() === selectEnd.toDateString() }
  function isToday(d: Date | null)  { return !!d && d.toDateString() === new Date().toDateString() }
  function inSel(d: Date | null) {
    if (!d || !selectStart) return false
    const terminus = selectEnd ?? hoverDate
    if (!terminus) return false
    const lo = selectStart < terminus ? selectStart : terminus
    const hi = selectStart < terminus ? terminus   : selectStart
    return d > lo && d < hi
  }

  return (
    <div style={{ width: 220 }}>
      {/* Month nav */}
      <div className="flex items-center justify-between mb-3">
        <button onClick={onPrevMonth} className="p-1 rounded transition-colors hover:text-white" style={{ color: '#555' }}>
          <ChevronLeft size={14} />
        </button>
        <span className="text-xs font-bold text-white">{MONTHS[month]} {year}</span>
        <button onClick={onNextMonth} className="p-1 rounded transition-colors hover:text-white" style={{ color: '#555' }}>
          <ChevronRight size={14} />
        </button>
      </div>
      {/* Day headers */}
      <div className="grid grid-cols-7 mb-1">
        {DAYS.map(d => (
          <div key={d} className="text-center" style={{ fontSize: 10, color: '#555', paddingBottom: 4 }}>{d}</div>
        ))}
      </div>
      {/* Day cells */}
      <div className="grid grid-cols-7 gap-y-0.5">
        {cells.map((d, i) => {
          if (!d) return <div key={i} />
          const start = isStart(d)
          const end   = isEnd(d)
          const inR   = inSel(d)
          const today = isToday(d)
          return (
            <button
              key={i}
              onClick={() => onDayClick(d)}
              onMouseEnter={() => onDayHover(d)}
              className="flex items-center justify-center transition-all"
              style={{
                height: 28, width: '100%', fontSize: 11, borderRadius: 6,
                background: start || end ? '#ffc600' : inR ? 'rgba(255,198,0,0.15)' : 'transparent',
                color: start || end ? '#000' : today ? '#ffc600' : '#ccc',
                fontWeight: start || end || today ? 700 : 400,
                outline: today && !start && !end ? '1px solid rgba(255,198,0,0.4)' : 'none',
              }}>
              {d.getDate()}
            </button>
          )
        })}
      </div>
    </div>
  )
}

// ── Main Picker ────────────────────────────────────────────────
export default function DateRangePicker() {
  const { range, setRange } = useDateRange()
  const [open, setOpen]     = useState(false)
  const [draft, setDraft]   = useState<{ preset: DateRangePreset; start: Date | null; end: Date | null }>({
    preset: range.preset, start: range.start, end: range.end,
  })
  const [hoverDate, setHoverDate]     = useState<Date | null>(null)
  const [pickingEnd, setPickingEnd]   = useState(false)
  const [viewDate, setViewDate]       = useState(new Date())
  const ref = useRef<HTMLDivElement>(null)

  // Close on outside click
  useEffect(() => {
    if (!open) return
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [open])

  function openPicker() {
    setDraft({ preset: range.preset, start: range.start, end: range.end })
    setPickingEnd(false)
    setHoverDate(null)
    const base = range.start ?? new Date()
    setViewDate(new Date(base.getFullYear(), base.getMonth(), 1))
    setOpen(true)
  }

  function selectPreset(preset: DateRangePreset) {
    if (preset === 'custom') {
      setDraft(d => ({ ...d, preset: 'custom' }))
      return
    }
    const built = buildPreset(preset)
    setDraft({ preset, start: built.start, end: built.end })
  }

  function handleDayClick(d: Date) {
    if (!pickingEnd) {
      setDraft({ preset: 'custom', start: d, end: null })
      setPickingEnd(true)
    } else {
      const [s, e] = d < (draft.start ?? d)
        ? [d, draft.start!]
        : [draft.start!, d]
      setDraft({ preset: 'custom', start: s, end: e })
      setPickingEnd(false)
    }
  }

  function applyDraft() {
    const built = draft.preset === 'custom'
      ? buildPreset('custom', draft.start, draft.end)
      : buildPreset(draft.preset)
    setRange(built)
    setOpen(false)
  }

  function clear() {
    const all = buildPreset('allTime')
    setRange(all)
    setDraft({ preset: 'allTime', start: null, end: null })
    setOpen(false)
  }

  function prevMonth() { setViewDate(d => new Date(d.getFullYear(), d.getMonth() - 1, 1)) }
  function nextMonth() { setViewDate(d => new Date(d.getFullYear(), d.getMonth() + 1, 1)) }

  const isCustom = draft.preset === 'custom'

  // Input helpers for custom range typing
  function toInputVal(d: Date | null) {
    if (!d) return ''
    return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`
  }
  function fromInput(s: string): Date | null {
    const d = new Date(s)
    return isNaN(d.getTime()) ? null : new Date(d.getFullYear(), d.getMonth(), d.getDate())
  }

  return (
    <div ref={ref} className="relative">
      {/* Trigger button */}
      <button
        onClick={openPicker}
        className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm transition-all"
        style={{
          background: open ? 'rgba(255,198,0,0.12)' : '#0f0f0f',
          border: `1px solid ${open ? 'rgba(255,198,0,0.4)' : 'rgba(255,198,0,0.15)'}`,
          color: '#ffc600',
        }}
        onMouseEnter={e => { if (!open) { e.currentTarget.style.borderColor = 'rgba(255,198,0,0.3)' }}}
        onMouseLeave={e => { if (!open) { e.currentTarget.style.borderColor = 'rgba(255,198,0,0.15)' }}}>
        <CalendarDays size={13} />
        <span className="font-semibold" style={{ fontSize: 12 }}>{range.label}</span>
        {range.preset !== 'allTime' && (
          <span
            onClick={e => { e.stopPropagation(); clear() }}
            className="ml-0.5 opacity-60 hover:opacity-100 transition-opacity cursor-pointer">
            <X size={11} />
          </span>
        )}
      </button>

      {/* Dropdown */}
      {open && (
        <div
          className="absolute right-0 top-11 z-50 rounded-2xl overflow-hidden"
          style={{
            background: '#0f0f0f',
            border: '1px solid rgba(255,198,0,0.2)',
            boxShadow: '0 20px 60px rgba(0,0,0,0.8)',
            minWidth: 460,
          }}>
          <div className="flex">
            {/* Left — presets */}
            <div className="py-3 border-r" style={{ width: 180, borderColor: 'rgba(255,198,0,0.1)' }}>
              <p className="px-4 pb-2 text-xs font-bold" style={{ color: '#555' }}>DATE RANGE</p>
              {PRESETS.map(p => (
                <button
                  key={p.id}
                  onClick={() => selectPreset(p.id)}
                  className="w-full text-left px-4 py-2 text-sm transition-all"
                  style={{
                    color: draft.preset === p.id ? '#ffc600' : '#a0a0a0',
                    background: draft.preset === p.id ? 'rgba(255,198,0,0.08)' : 'transparent',
                    fontWeight: draft.preset === p.id ? 600 : 400,
                    borderLeft: draft.preset === p.id ? '2px solid #ffc600' : '2px solid transparent',
                  }}
                  onMouseEnter={e => { if (draft.preset !== p.id) { e.currentTarget.style.color = '#fff'; e.currentTarget.style.background = 'rgba(255,255,255,0.04)' }}}
                  onMouseLeave={e => { if (draft.preset !== p.id) { e.currentTarget.style.color = '#a0a0a0'; e.currentTarget.style.background = 'transparent' }}}>
                  {p.label}
                </button>
              ))}
            </div>

            {/* Right — calendar / summary */}
            <div className="flex-1 p-5">
              {isCustom ? (
                <div className="space-y-4">
                  {/* Date inputs */}
                  <div className="grid grid-cols-2 gap-3">
                    {(['start', 'end'] as const).map(which => (
                      <div key={which}>
                        <label className="block text-xs font-semibold mb-1" style={{ color: '#555' }}>
                          {which === 'start' ? 'From' : 'To'}
                        </label>
                        <input
                          type="date"
                          value={toInputVal(which === 'start' ? draft.start : draft.end)}
                          onChange={e => {
                            const d = fromInput(e.target.value)
                            setDraft(f => ({ ...f, [which]: d }))
                            if (d) setViewDate(new Date(d.getFullYear(), d.getMonth(), 1))
                          }}
                          className="w-full rounded-lg px-3 py-2 text-sm text-white outline-none focus:ring-1 focus:ring-yellow-400/50"
                          style={{ background: '#1a1a1a', border: '1px solid rgba(255,198,0,0.2)' }}
                        />
                      </div>
                    ))}
                  </div>
                  {/* Calendar */}
                  <MiniCalendar
                    viewDate={viewDate}
                    onPrevMonth={prevMonth}
                    onNextMonth={nextMonth}
                    selectStart={draft.start}
                    selectEnd={draft.end}
                    hoverDate={hoverDate}
                    onDayClick={handleDayClick}
                    onDayHover={setHoverDate}
                  />
                  {pickingEnd && (
                    <p className="text-xs text-center" style={{ color: '#555' }}>Click a second date to set the end</p>
                  )}
                </div>
              ) : (
                // Summary for non-custom presets
                <div className="flex flex-col justify-between h-full">
                  <div>
                    <p className="text-xs font-bold mb-4" style={{ color: '#555' }}>SELECTED PERIOD</p>
                    <div className="space-y-3">
                      {(['start', 'end'] as const).map(which => {
                        const built = buildPreset(draft.preset)
                        const d = which === 'start' ? built.start : built.end
                        return (
                          <div key={which} className="rounded-xl px-4 py-3" style={{ background: '#1a1a1a' }}>
                            <p className="text-xs mb-0.5" style={{ color: '#555' }}>{which === 'start' ? 'From' : 'To'}</p>
                            <p className="text-sm font-semibold text-white">
                              {d ? d.toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' }) : 'Beginning of time'}
                            </p>
                          </div>
                        )
                      })}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between px-4 py-3 border-t" style={{ borderColor: 'rgba(255,198,0,0.1)' }}>
            <button
              onClick={() => setOpen(false)}
              className="px-4 py-2 rounded-xl text-sm font-semibold transition-all"
              style={{ color: '#a0a0a0' }}
              onMouseEnter={e => (e.currentTarget.style.color = '#fff')}
              onMouseLeave={e => (e.currentTarget.style.color = '#a0a0a0')}>
              Cancel
            </button>
            <div className="flex items-center gap-2">
              <span className="text-xs" style={{ color: '#555' }}>
                {draft.preset !== 'allTime' && (() => {
                  const b = draft.preset === 'custom'
                    ? buildPreset('custom', draft.start, draft.end)
                    : buildPreset(draft.preset)
                  return b.start ? b.start.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' }) + ' – ' + (b.end?.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) ?? '?') : ''
                })()}
              </span>
              <button
                onClick={applyDraft}
                disabled={draft.preset === 'custom' && (!draft.start || !draft.end)}
                className="px-5 py-2 rounded-xl text-sm font-bold transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                style={{ background: '#ffc600', color: '#000' }}
                onMouseEnter={e => { if (!e.currentTarget.disabled) e.currentTarget.style.background = '#e6b200' }}
                onMouseLeave={e => (e.currentTarget.style.background = '#ffc600')}>
                Apply
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
